import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { Divider } from '@/design-system/components/Divider';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { GetBrokersResponseItem } from '@utx/utx-client';
import ExchangeBanner from '../../../../assets/svg/ExchangeBanner.svg';
import { ErrorCode } from '../../enum';
import { ExchangeSubAccountStackRoutes } from '../../exchange.navigator';
import { useExchangeSubAccount } from '../../hooks/useExchangeSubAccount.hook';
import { useGetCalcFee } from '../../hooks/useGetCalcFee.hook';
import { useNavigatorPage } from '../../hooks/useNavigatorPage.hook';
import { ScaledImage } from '../components/ScaledImage';
export interface SelectExchangeProps {
  data: GetBrokersResponseItem;
  shouldBeFetchFee: boolean;
  onClose: () => void;
}

export const ExchangeConfirmForm: React.FC<SelectExchangeProps> = (props) => {
  const { onClose, data, shouldBeFetchFee } = props;
  const { t } = useTranslation('exchange-sub-account');
  const exchangeSubAccount = useExchangeSubAccount();
  const calcFee = useGetCalcFee({ enabledFetchAppFee: false, enabledFetchMonthlyFee: false });
  const navigator = useNavigatorPage();
  const {
    monthlyData: { fee: monthlyFee },
    applicationData: { fee: applicationFee }
  } = calcFee;
  const {
    general: {
      wallets,
      walletId,
      walletLabel,
      alertMessage,
      balance,
      assetName,
      setAlertMessage,
      setWalletId,
      setAssetName,
      getAccountType
    },
    postOpenAccount: { isLoading: loadingOpenAccount }
  } = exchangeSubAccount;
  const { userFee: applicationUserFee, feeCurrency: applicationFeeCurrency } = applicationFee || {};
  const { userFee: monthlyUserFee, feeCurrency: monthlyFeeCurrency } = monthlyFee || {};
  const metadata = data.metadata || {};
  const { feeSummary } = metadata || {};
  const { estimatedAccountOpeningTime } = feeSummary || {};
  const {
    topHeader,
    body,
    viewBinance,
    viewBinanceBackground,
    viewDropdownAbsolute,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textValueDebitAmount,
    viewBinanceWrapper,
    textBinance,
    textMonthlyFeeNote,
    button,
    iconWrapper,
    textInsufficient
  } = sxStyles;

  useEffect(() => {
    onUpdateAssetName();
    onRefetchFee();
  }, []);

  useEffect(() => {
    onUpdateAssetName();
  }, [applicationFeeCurrency]);

  const onRefetchFee = () => {
    if (shouldBeFetchFee) {
      calcFee.applicationData.refetch();
      calcFee.monthlyData.refetch();
    }
  };

  const onUpdateAssetName = () => {
    if (!_.isEmpty(applicationFeeCurrency)) {
      setAssetName(applicationFeeCurrency);
    }
  };

  const displayAmountWithCurrency = (amountString: string, currency: string) => {
    const amount = parseFloat(amountString);
    return `${amount < 0 ? '-' : ''}${formatAmountByDecimals({ value: Math.abs(amount), decimals: 2 })} ${currency ?? 'HKD'}`;
  };

  const onConfirm = () => {
    setAlertMessage('');
    exchangeSubAccount.postOpenAccount.postOpenAccountApi(
      {
        brokerId: data.id,
        walletId: walletId
      },
      (data) => {
        onClose();
        setTimeout(() => {
          navigator.replaceScreen(RootStackRoutes.ExchangeSubAccount, ExchangeSubAccountStackRoutes.InstructionSubmitted);
        }, 350);
      },
      (error) => {
        var statusCode: number | undefined = error.response?.status;
        if (statusCode === ErrorCode.EXPIRED_TOKEN_ERROR) {
          onClose();
        } else {
          setAlertMessage(error.response?.data?.message ?? '');
        }
      }
    );
  };

  const applicationFeeAmount = applicationFee?.userFee ?? '0';
  const balanceAmount = parseFloat(balance);
  const debitAmount = parseFloat(applicationFeeAmount);

  const getIcon = (logoUrl: string) => {
    return (
      <View sx={iconWrapper}>
        <ScaledImage uri={logoUrl ?? ''} />
      </View>
    );
  };

  return (
    <ScrollView>
      <View>
        <View sx={topHeader}>
          <BottomSheetHeader
            title={t('exchange-account::select-exchange-account.title--application-instruction-confirmation')}
            onPress={onClose}
            sx={{ paddingX: 28, pb: 0 }}
          />
        </View>
        <Divider />
        <View sx={body}>
          <TextBody semiBold>{t('exchange-account::confirmation.text--you-have-selected-account')}</TextBody>
          <View sx={viewBinance}>
            <View sx={viewBinanceBackground}>
              <ExchangeBanner width={'100%'} height={'100%'} />
            </View>
            <View sx={viewBinanceWrapper}>
              <H6 bold sx={textBinance}>
                {data.name}
              </H6>
              {getIcon(data.logoUrl)}
            </View>
          </View>

          <View sx={{ zIndex: 1000, mb: -10 }}>
            <AccountSelector asset={assetName} onValueChange={setWalletId} value={walletId} />
          </View>

          <View
            sx={{
              ...horizontalWrapper,
              ...{ mb: '$5' }
            }}>
            <TextCaption sx={textLabel}>{t('exchange-account::glossary.balance')}</TextCaption>
            <TextCaption>{displayAmountWithCurrency(balance, assetName)}</TextCaption>
          </View>
          <View
            sx={{
              ...horizontalWrapper,
              ...{ mb: '$3' }
            }}>
            <TextBody sx={textLabel}>{t('exchange-account::glossary.account-application-fee')}</TextBody>
            <TextBody sx={{ color: '$danger' }}>- {displayAmountWithCurrency(applicationUserFee ?? '0', assetName)}</TextBody>
          </View>
          <View
            sx={{
              ...horizontalWrapper,
              ...{ mb: '$3' }
            }}>
            <TextBody sx={textLabel}>{t('exchange-account::glossary.est-account-opening-time')}</TextBody>
            <TextBody>{estimatedAccountOpeningTime ?? '-'}</TextBody>
          </View>
          <View sx={{ mb: '$5' }}>
            <View
              sx={{
                ...horizontalWrapper,
                ...{ mb: '$1' }
              }}>
              <TextBody sx={textLabel}>{t('exchange-account::glossary.monthly-fee')}</TextBody>
              <TextBody>
                {displayAmountWithCurrency(monthlyUserFee ?? '0', !_.isEmpty(monthlyFeeCurrency) ? monthlyFeeCurrency : assetName)}
              </TextBody>
            </View>
            <TextBody sx={textMonthlyFeeNote}>{t('general::deducted-end-of-each-month')}</TextBody>
          </View>
          <Divider />
          <TextCaption semiBold sx={textDebitAmount}>
            {t('exchange-account::glossary.debit-amount')}
          </TextCaption>
          <H6 bold sx={textValueDebitAmount}>
            - {displayAmountWithCurrency(`${debitAmount}`, assetName)}
          </H6>
          {!_.isEmpty(walletId) && (
            <TextBody>{t('exchange-account::glossary.will-be-deducted', { wallet: getAccountType(walletLabel) })}</TextBody>
          )}
          {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: '$1' }} />}
          {(balanceAmount < debitAmount || wallets.length == 0) && !_.isEmpty(walletId) && (
            <TextBody sx={textInsufficient}>{t('exchange-account::account-selection.text--insufficient-balance')}</TextBody>
          )}
          <UniButton
            label={t('general::confirm')}
            onPress={onConfirm}
            sx={button}
            size="medium"
            disabled={balanceAmount < debitAmount || wallets.length == 0 || _.isEmpty(walletId)}
            loading={loadingOpenAccount}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    paddingX: '$4',
    backgroundColor: '$backgroundPrimary'
  },
  topHeader: {
    height: 76,
    justifyContent: 'center'
  },
  body: {
    minHeight: 200,
    px: 28,
    py: 28
  },
  viewBinance: {
    height: 70,
    gap: 20,
    borderRadius: '$radius2',
    my: '$5',
    overflow: 'hidden'
  },
  viewFilterDropdown: {
    width: '100%',
    borderRadius: '$radius1',
    borderColor: '#CCCCCC',
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: '$4',
    my: '$1',
    height: 48,
    zIndex: 1000
  },
  horizontalWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  textLabel: {
    color: '$textSecondary'
  },
  textDebitAmount: {
    mt: '$5',
    mb: '$1',
    color: '$textPrimary'
  },
  textValueDebitAmount: {
    mb: '$1',
    color: '$danger'
  },
  button: {
    height: 44,
    mt: '$10'
  },
  viewBinanceBackground: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  viewBinanceWrapper: {
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    px: '$5'
  },
  textBinance: {
    color: '$textDarkBackground'
  },
  viewDropdownAbsolute: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: '$4',
    height: '$11'
  },
  textMonthlyFeeNote: {
    color: '$textSecondary'
  },
  iconWrapper: {
    height: '100%',
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  textInsufficient: {
    color: '$danger',
    mt: '$3'
  }
};
